import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import UserSearchModal, {
  type User,
} from '../../../Components/UserSearchModal';
import PersonalizationMemberPicker from '../../ChapterSuppliesStorefront/PersonalizationMemberPicker';
import { getCountries, getStates } from '../../../endpoints/lookupsTs';
import {
  createManagementMailInCheckOrder,
  getOnlineItem,
  listOnlineItems,
} from '../../../endpoints/chapterSupplies';
import { getUserPersonalInfo } from '../../../endpoints/usersTs';
import type { OnlineItemResponse } from '../../../types/chapterSupplies';
import {
  useApiGet,
  useApiSend,
  getApiErrorMessage,
} from '../../../utils/httpClientTs';
import type { ManagementMailInCheckOrderRequest } from '../../../types/chapterSupplies';

const getFreight = (subtotal: number): number => {
  if (subtotal < 10) return 5;
  if (subtotal <= 20) return 8;
  return 10;
};

/** Same primary physical address type as storefront checkout */
const PRIMARY_SHIPPING_ADDRESS_TYPE_ID = 'd5107f490a3049d5969ccbb5f843aac7';

type DraftLine = {
  key: string;
  online_item_id: string;
  online_item_variant_id: string;
  quantity: number;
  custom_text_raw: string;
  personalization_user_id: string | null;
  personalization_display_name: string | null;
  personalization_full_name: string | null;
};

function newLine(): DraftLine {
  return {
    key: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    online_item_id: '',
    online_item_variant_id: '',
    quantity: 1,
    custom_text_raw: '',
    personalization_user_id: null,
    personalization_display_name: null,
    personalization_full_name: null,
  };
}

function LineEditor({
  line,
  purchaserId,
  items,
  onChange,
  onRemove,
}: {
  line: DraftLine;
  purchaserId: string;
  items: OnlineItemResponse[];
  onChange: (next: DraftLine) => void;
  onRemove: () => void;
}) {
  const { data: detail } = useApiGet(
    ['mgmt-mail-in-online-item', line.online_item_id],
    () => getOnlineItem(line.online_item_id),
    { enabled: !!line.online_item_id.trim() }
  );

  const selectedSummary = items.find((i) => i.id === line.online_item_id);
  const needsVariant = (detail?.has_variants ?? 0) !== 0;
  const customizationOn =
    (detail?.allows_custom_text ?? 0) !== 0 ||
    (detail?.allows_personalization ?? 0) !== 0;
  const personalizationOn = (detail?.allows_personalization ?? 0) !== 0;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Line item
          </Typography>
          <IconButton aria-label="Remove line" onClick={onRemove} size="small">
            <DeleteOutlineIcon />
          </IconButton>
        </Stack>
        <Autocomplete
          options={items}
          getOptionLabel={(o) => o.item_name?.trim() || o.name?.trim() || o.id}
          value={items.find((o) => o.id === line.online_item_id) ?? null}
          onChange={(_, v) => {
            onChange({
              ...line,
              online_item_id: v?.id || '',
              online_item_variant_id: '',
              custom_text_raw: '',
              personalization_user_id: null,
              personalization_display_name: null,
              personalization_full_name: null,
            });
          }}
          renderInput={(params) => (
            <TextField {...params} label="Online item" size="small" />
          )}
          sx={{ mb: 2 }}
        />
        {line.online_item_id.trim() && detail && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {selectedSummary?.price != null && !needsVariant && (
                <>List price: ${Number(selectedSummary.price).toFixed(2)}</>
              )}
              {(detail.order_approval_required ?? 0) !== 0 && (
                <> — Order approval required</>
              )}
            </Typography>
            {needsVariant && (
              <FormControl size="small" fullWidth>
                <InputLabel id={`variant-${line.key}`}>Variant</InputLabel>
                <Select
                  labelId={`variant-${line.key}`}
                  label="Variant"
                  value={line.online_item_variant_id}
                  onChange={(e) =>
                    onChange({
                      ...line,
                      online_item_variant_id: String(e.target.value),
                    })
                  }
                >
                  {(detail.variants || []).map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.item_name || v.description || v.id} — $
                      {Number(v.price).toFixed(2)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              size="small"
              type="number"
              label="Quantity"
              value={line.quantity}
              onChange={(e) => {
                const q = Number(e.target.value);
                onChange({
                  ...line,
                  quantity: Number.isFinite(q) && q >= 1 ? Math.floor(q) : 1,
                });
              }}
              inputProps={{ min: 1 }}
            />
            {customizationOn && (
              <TextField
                size="small"
                label="Custom text (one line per row)"
                value={line.custom_text_raw}
                onChange={(e) =>
                  onChange({ ...line, custom_text_raw: e.target.value })
                }
                multiline
                minRows={2}
                helperText={
                  detail.max_text_length && detail.max_text_lines
                    ? `Up to ${detail.max_text_lines} lines, ${detail.max_text_length} characters each`
                    : undefined
                }
              />
            )}
            {personalizationOn && purchaserId && (
              <PersonalizationMemberPicker
                selectedUserID={line.personalization_user_id}
                selectedDisplayName={line.personalization_display_name}
                fullNameValue={line.personalization_full_name}
                maxChars={255}
                onChange={(next) => {
                  onChange({
                    ...line,
                    personalization_user_id: next.personalization_user_id,
                    personalization_display_name:
                      next.personalization_display_name,
                    personalization_full_name: next.personalization_full_name,
                  });
                }}
              />
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default function ManagementMailInCheckOrder() {
  const navigate = useNavigate();
  const [purchaserModal, setPurchaserModal] = useState(false);
  const [purchaser, setPurchaser] = useState<User | null>(null);
  const [lines, setLines] = useState<DraftLine[]>(() => [newLine()]);
  const [shipToName, setShipToName] = useState('');
  const [shipToAddress1, setShipToAddress1] = useState('');
  const [shipToAddress2, setShipToAddress2] = useState('');
  const [shipToCity, setShipToCity] = useState('');
  const [shipToPostalCode, setShipToPostalCode] = useState('');
  const [shipToStateID, setShipToStateID] = useState('');
  const [shipToCountryID, setShipToCountryID] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [overrideShipTo, setOverrideShipTo] = useState(false);
  const [checkNumber, setCheckNumber] = useState('');
  const [checkMemo, setCheckMemo] = useState('');

  const purchaserId = (purchaser?.id || '').trim();

  const { data: profile, isLoading: profileLoading } = useApiGet(
    ['chapter-supplies-mail-in-purchaser-profile', purchaserId],
    () => getUserPersonalInfo(purchaserId),
    { enabled: !!purchaserId }
  );

  const primaryAddress = useMemo(() => {
    const addrs = profile?.addresses;
    if (!addrs?.length) return undefined;
    return (
      addrs.find(
        (a) => a.address_type_id === PRIMARY_SHIPPING_ADDRESS_TYPE_ID
      ) || addrs[0]
    );
  }, [profile]);

  const resolvedName = useMemo(() => {
    const fromProfile =
      profile &&
      `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
    if (fromProfile) return fromProfile;
    return purchaser
      ? (
          purchaser.full_name ||
          `${purchaser.first_name || ''} ${purchaser.last_name || ''}`.trim()
        ).trim()
      : '';
  }, [profile, purchaser]);

  const resolvedAddress1 = primaryAddress?.address_1 || '';
  const resolvedAddress2 = primaryAddress?.address_2 || '';
  const resolvedCity = primaryAddress?.city || '';
  const resolvedPostal = primaryAddress?.postal_code || '';
  const resolvedStateID = primaryAddress?.state_id || '';
  const resolvedCountryID = primaryAddress?.country_id || '';

  const selectedCountryID = overrideShipTo
    ? shipToCountryID
    : resolvedCountryID;

  useEffect(() => {
    setOverrideShipTo(false);
    setShipToName('');
    setShipToAddress1('');
    setShipToAddress2('');
    setShipToCity('');
    setShipToPostalCode('');
    setShipToStateID('');
    setShipToCountryID('');
  }, [purchaserId]);

  const { data: onlineList } = useApiGet(
    ['chapter-supplies-mail-in-online-items'],
    () =>
      listOnlineItems({
        limit: 500,
        sort_by: 'item_name',
        sort_order: 'asc',
      })
  );
  const items = useMemo(() => onlineList?.online_items ?? [], [onlineList]);

  const { data: countries = [] } = useApiGet(
    ['mgmt-mail-in-countries'],
    getCountries
  );
  const { data: states = [] } = useApiGet(
    ['mgmt-mail-in-states', selectedCountryID],
    () => getStates(selectedCountryID),
    { enabled: !!selectedCountryID.trim() }
  );

  const purchaserLabel = purchaser
    ? (
        purchaser.full_name ||
        `${purchaser.first_name || ''} ${purchaser.last_name || ''}`.trim() ||
        purchaser.id
      ).trim()
    : '';

  const linesComplete = lines.every((l) => {
    if (!l.online_item_id.trim()) return false;
    const row = items.find((i) => i.id === l.online_item_id);
    if (!row) return false;
    if ((row.has_variants ?? 0) !== 0 && !l.online_item_variant_id.trim()) {
      return false;
    }
    return l.quantity >= 1;
  });

  const effectiveShipTo = useMemo(() => {
    if (overrideShipTo) {
      return {
        ship_to_name: shipToName,
        ship_to_address_line1: shipToAddress1,
        ship_to_address_line2: shipToAddress2,
        ship_to_city: shipToCity,
        ship_to_state_id: shipToStateID,
        ship_to_postal_code: shipToPostalCode,
        ship_to_country_id: shipToCountryID,
      };
    }
    return {
      ship_to_name: resolvedName,
      ship_to_address_line1: resolvedAddress1,
      ship_to_address_line2: resolvedAddress2,
      ship_to_city: resolvedCity,
      ship_to_state_id: resolvedStateID,
      ship_to_postal_code: resolvedPostal,
      ship_to_country_id: resolvedCountryID,
    };
  }, [
    overrideShipTo,
    shipToName,
    shipToAddress1,
    shipToAddress2,
    shipToCity,
    shipToStateID,
    shipToPostalCode,
    shipToCountryID,
    resolvedName,
    resolvedAddress1,
    resolvedAddress2,
    resolvedCity,
    resolvedStateID,
    resolvedPostal,
    resolvedCountryID,
  ]);

  const shipComplete =
    !!effectiveShipTo.ship_to_name.trim() &&
    !!effectiveShipTo.ship_to_address_line1.trim() &&
    !!effectiveShipTo.ship_to_city.trim() &&
    !!effectiveShipTo.ship_to_state_id.trim() &&
    !!effectiveShipTo.ship_to_postal_code.trim() &&
    !!effectiveShipTo.ship_to_country_id.trim();

  const canSubmit =
    !!purchaserId &&
    !!checkNumber.trim() &&
    lines.length > 0 &&
    linesComplete &&
    shipComplete &&
    (overrideShipTo || !profileLoading);

  const { mutate: submit, isPending } = useApiSend(
    createManagementMailInCheckOrder,
    (resp) => {
      setSubmitError(null);
      navigate(`/chapter-supplies-management/orders/${resp.order_id}`);
    },
    (err) => setSubmitError(getApiErrorMessage(err))
  );

  const buildPayload = (): ManagementMailInCheckOrderRequest => {
    const mappedLines = lines.map((l) => {
      const rawLines = l.custom_text_raw
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const row = items.find((i) => i.id === l.online_item_id);
      const needsVariant = (row?.has_variants ?? 0) !== 0;
      return {
        online_item_id: l.online_item_id.trim(),
        online_item_variant_id:
          needsVariant && l.online_item_variant_id.trim()
            ? l.online_item_variant_id.trim()
            : null,
        quantity: l.quantity,
        custom_text_lines: rawLines.length ? rawLines : undefined,
        personalization_user_id: l.personalization_user_id || null,
        personalization_first_name: l.personalization_full_name?.trim()
          ? l.personalization_full_name.trim()
          : null,
      };
    });

    const a2 = effectiveShipTo.ship_to_address_line2.trim();
    const memo = checkMemo.trim();
    return {
      user_id: purchaserId,
      check_number: checkNumber.trim(),
      check_memo: memo ? memo : null,
      ship_to_name: effectiveShipTo.ship_to_name.trim(),
      ship_to_address_line1: effectiveShipTo.ship_to_address_line1.trim(),
      ship_to_address_line2: a2 ? a2 : null,
      ship_to_city: effectiveShipTo.ship_to_city.trim(),
      ship_to_state_id: effectiveShipTo.ship_to_state_id.trim(),
      ship_to_postal_code: effectiveShipTo.ship_to_postal_code.trim(),
      ship_to_country_id: effectiveShipTo.ship_to_country_id.trim(),
      lines: mappedLines,
    };
  };

  const estimatedSubtotal = useMemo(() => {
    let s = 0;
    // Variant lines use parent list price when present; true total comes from checkout API.
    for (const l of lines) {
      if (!l.online_item_id.trim()) continue;
      const row = items.find((i) => i.id === l.online_item_id);
      const unit = row?.price != null ? Number(row.price) : 0;
      s += unit * Math.max(1, l.quantity);
    }
    return s;
  }, [lines, items]);

  const estFreight = getFreight(estimatedSubtotal);

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/chapter-supplies-management/orders')}
        sx={{ mb: 2 }}
      >
        Back to orders
      </Button>

      <Typography variant="h4" sx={{ mb: 2 }}>
        Mail-in order (check received)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create a storefront order paid by check for a member whose payment you
        have already received. Status will be Processing if any item requires
        approval, otherwise Paid.
      </Typography>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Purchaser (member)
          </Typography>
          <Button variant="outlined" onClick={() => setPurchaserModal(true)}>
            {purchaserLabel || 'Search member'}
          </Button>
          <UserSearchModal
            modalOpen={purchaserModal}
            setModalOpen={setPurchaserModal}
            userObjectRet
            setUser={(u) => {
              setPurchaser(u);
              setPurchaserModal(false);
            }}
            activeMembers
            statusFilter="active"
            allowSetToMe
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Check (for finance verification)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Stored with the order so the check can be matched in downstream
            verification.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Check number"
              value={checkNumber}
              onChange={(e) => setCheckNumber(e.target.value)}
              size="small"
              fullWidth
              required
              inputProps={{ maxLength: 32 }}
              helperText="Required. Up to 32 characters."
            />
            <TextField
              label="Memo (optional)"
              value={checkMemo}
              onChange={(e) => setCheckMemo(e.target.value)}
              size="small"
              fullWidth
              multiline
              minRows={2}
              helperText="Optional note for the finance team."
            />
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Ship to
          </Typography>
          {purchaserId && profileLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Loading member address…
            </Typography>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={overrideShipTo}
                onChange={(e) => {
                  const v = e.target.checked;
                  setOverrideShipTo(v);
                  if (v) {
                    setShipToName(resolvedName);
                    setShipToAddress1(resolvedAddress1);
                    setShipToAddress2(resolvedAddress2);
                    setShipToCity(resolvedCity);
                    setShipToPostalCode(resolvedPostal);
                    setShipToStateID(resolvedStateID);
                    setShipToCountryID(resolvedCountryID);
                  }
                }}
              />
            }
            label="Override default shipping address"
          />
          {!overrideShipTo &&
            purchaserId &&
            !profileLoading &&
            !primaryAddress && (
              <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
                No address on file for this member. Turn on the override above
                to enter shipping.
              </Alert>
            )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Ship to name"
              value={overrideShipTo ? shipToName : resolvedName}
              onChange={(e) => setShipToName(e.target.value)}
              disabled={!overrideShipTo}
              size="small"
              fullWidth
            />
            <TextField
              label="Address line 1"
              value={overrideShipTo ? shipToAddress1 : resolvedAddress1}
              onChange={(e) => setShipToAddress1(e.target.value)}
              disabled={!overrideShipTo}
              size="small"
              fullWidth
            />
            <TextField
              label="Address line 2 (optional)"
              value={overrideShipTo ? shipToAddress2 : resolvedAddress2}
              onChange={(e) => setShipToAddress2(e.target.value)}
              disabled={!overrideShipTo}
              size="small"
              fullWidth
            />
            <TextField
              label="City"
              value={overrideShipTo ? shipToCity : resolvedCity}
              onChange={(e) => setShipToCity(e.target.value)}
              disabled={!overrideShipTo}
              size="small"
              fullWidth
            />
            <FormControl size="small" fullWidth disabled={!overrideShipTo}>
              <InputLabel id="mail-in-country">Country</InputLabel>
              <Select
                labelId="mail-in-country"
                label="Country"
                value={overrideShipTo ? shipToCountryID : resolvedCountryID}
                onChange={(e) => {
                  const next = String(e.target.value || '');
                  setShipToCountryID(next);
                  setShipToStateID('');
                }}
              >
                {countries.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              fullWidth
              disabled={!overrideShipTo || !selectedCountryID}
            >
              <InputLabel id="mail-in-state">State / Province</InputLabel>
              <Select
                labelId="mail-in-state"
                label="State / Province"
                value={overrideShipTo ? shipToStateID : resolvedStateID}
                onChange={(e) => setShipToStateID(String(e.target.value))}
              >
                {states.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Postal code"
              value={overrideShipTo ? shipToPostalCode : resolvedPostal}
              onChange={(e) => setShipToPostalCode(e.target.value)}
              disabled={!overrideShipTo}
              size="small"
              fullWidth
            />
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Items
      </Typography>
      {lines.map((line, idx) => (
        <LineEditor
          key={line.key}
          line={line}
          purchaserId={purchaserId}
          items={items}
          onChange={(next) => {
            setLines((prev) => prev.map((p, i) => (i === idx ? next : p)));
          }}
          onRemove={() => {
            setLines((prev) => prev.filter((_, i) => i !== idx));
          }}
        />
      ))}
      <Button
        startIcon={<AddIcon />}
        onClick={() => setLines((p) => [...p, newLine()])}
        sx={{ mb: 2 }}
      >
        Add line
      </Button>

      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary">
        Estimated subtotal ${estimatedSubtotal.toFixed(2)} + freight $
        {estFreight.toFixed(2)} (final totals set by server)
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          disabled={!canSubmit || isPending}
          onClick={() => submit(buildPayload())}
        >
          {isPending ? 'Creating…' : 'Create order'}
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/chapter-supplies-management/orders')}
        >
          Cancel
        </Button>
      </Stack>
    </Box>
  );
}
